import { useRef } from 'react';
import type { ChatUiTrip } from '@voll-intelligence/genui-contract';
import { AirplaneTakeoffIcon } from '@phosphor-icons/react';
import {
  SidePanel,
  SidePanelBody,
  SidePanelContent,
  SidePanelHeader,
} from '@voll-intelligence/ui-kit/ui/side-panel';
import { useGenUiI18n } from '../../../../shared/ui/i18n.js';
import { fmtDateRange, fmtMoney } from '../../../../shared/trip/trip_format.js';
import { modalLabel } from '../../../../shared/trip/trip_maps.js';
import { KvGrid, TripStatusBadge } from '../../../../shared/trip/trip_primitives.js';
import { VoucherAccordion } from '../../../../shared/trip/voucher_accordion.js';

/**
 * Drawer de detalhe de uma viagem (`get_trip`): resumo da viagem + acordeão de
 * vouchers com detalhe por modal, no `SidePanel` do ui-kit. Foco, trap, Esc,
 * clique fora e o recuo do recorte do aparelho (status bar, notch, home
 * indicator) são do painel — abaixo de `sm` ele é a tela inteira e o ✕ é a
 * única saída por toque (#5114). Quando a viagem não tem vouchers, mostra o
 * estado "em processamento".
 */
export function TripDrawer({
  trip,
  onClose,
}: {
  readonly trip: ChatUiTrip;
  readonly onClose: () => void;
}) {
  const { t, locale } = useGenUiI18n();
  const panelRef = useRef<HTMLDivElement>(null);

  const dateRange = fmtDateRange(trip.startLocal, trip.endLocal, trip.startAt, trip.endAt, locale);
  const hasRoute = trip.origin != null && trip.destination != null;
  const subtitleParts = [
    hasRoute ? `${String(trip.origin)} → ${String(trip.destination)}` : null,
    dateRange,
  ].filter(Boolean);
  const requester = trip.vouchers.find((v) => v.requester?.name)?.requester?.name ?? null;
  const modalLabels = trip.modals
    .map((m) => modalLabel(m, t))
    .filter(Boolean)
    .join(' · ');

  return (
    <SidePanel
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SidePanelContent
        ref={panelRef}
        // O foco entra no próprio painel: o leitor de tela anuncia a viagem
        // antes do primeiro controle.
        initialFocus={panelRef}
        size="medium"
        closeLabel={t('common.close')}
        aria-label={trip.name ?? t('trips.drawer.ariaFallback')}
        className="gap-0"
      >
        {/* `pr-12` deixa a faixa do ✕ do painel livre para o código da viagem. */}
        <SidePanelHeader className="border-b border-border pr-12">
          <span className="tdw-code truncate">{trip.tripId}</span>
        </SidePanelHeader>

        <SidePanelBody className="gap-4 pt-4">
          <div className="tdw-hero">
            <span className="tdw-hero-ico">
              <AirplaneTakeoffIcon size={19} aria-hidden />
            </span>
            <div className="tdw-hero-id">
              <h3 className="tdw-title">{trip.name ?? t('trips.unnamed')}</h3>
              {subtitleParts.length > 0 && (
                <p className="tdw-subtitle">{subtitleParts.join(' · ')}</p>
              )}
            </div>
            <span className="tdw-hero-badge">
              <TripStatusBadge status={trip.status} />
            </span>
          </div>

          <section className="tdw-sec">
            <span className="tdw-sec-label">{t('trips.drawer.summary')}</span>
            <div className="tdw-grid">
              <KvGrid
                rows={[
                  { k: t('trips.drawer.period'), v: dateRange, empty: t('common.toDefine') },
                  {
                    k: t('trips.drawer.path'),
                    v: hasRoute ? `${String(trip.origin)} → ${String(trip.destination)}` : null,
                    empty: t('trips.routeTbd'),
                  },
                  { k: t('trips.drawer.modals'), v: modalLabels || null },
                  {
                    k: t('common.total'),
                    v: fmtMoney(trip.totalAmount, trip.currency),
                    empty: t('common.toConfirm'),
                  },
                  { k: t('trips.drawer.requester'), v: requester },
                  { k: t('trips.drawer.company'), v: trip.companyName ?? null },
                ]}
              />
            </div>
          </section>

          <section className="tdw-sec">
            <span className="tdw-sec-label">
              {t('trips.drawer.vouchers')}
              {trip.vouchers.length > 0 ? ` · ${String(trip.vouchers.length)}` : ''}
            </span>
            {trip.vouchers.length === 0 ? (
              <div className="tdw-state">
                <p className="t">{t('trips.drawer.processingTitle')}</p>
                <p className="b">{t('trips.drawer.processingBody')}</p>
              </div>
            ) : (
              <div className="vacc-list">
                {trip.vouchers.map((voucher, i) => (
                  <VoucherAccordion key={voucher.id} voucher={voucher} defaultOpen={i === 0} />
                ))}
              </div>
            )}
          </section>
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  );
}
